import type { Plugin } from '../../core/collector'
import type { Collector } from '../../core/collector'
import { isBrowser } from '../../core/utils'

export class StayPlugin implements Plugin {
  name = 'stay'
  version = '1.0.0'

  private collector!: Collector
  private pageEnterTime = 0
  private visibilityState = {
    isVisible: true,
    accumulatedTime: 0,
    lastVisibleTime: 0,
  }
  private currentUrl = ''
  private lastLocation = ''

  // 延时处理
  private sendTimer: ReturnType<typeof setTimeout> | null = null
  private delay = 20 // 默认延迟 20ms

  // 防止重复上报
  private hasReportedUnload = false

  install(collector: Collector) {
    this.collector = collector

    // enable: false 时完全退出（App 环境由 app-page-tracking.ts + JSBridge 接管）
    // 与 RoutePlugin 对齐，避免 stay 自动上报与 bridge 路径双源冲突
    const routerTrackingConfig = collector.config.get('routerTracking')
    if (routerTrackingConfig?.enable === false) return

    this.currentUrl = location.href
    this.lastLocation = location.href
    this.pageEnterTime = Date.now()

    this.initVisibility()
    this.bindPageUnload()

    // 监听路由变化
    const autoTrack = routerTrackingConfig?.autoTrack !== false
    const allowHash = routerTrackingConfig?.allowHash ?? false

    if (autoTrack) {
      this.hackHistoryAPI()
      this.bindNativeEvents(allowHash)
    }
  }

  private hackHistoryAPI() {
    if (!isBrowser()) return

    const originalPushState = history.pushState
    history.pushState = (state, title, url) => {
      const result = originalPushState.call(history, state, title, url)
      if (this.lastLocation !== location.href) this.handleRouteChange()
      return result
    }

    const originalReplaceState = history.replaceState
    history.replaceState = (state, title, url) => {
      const result = originalReplaceState.call(history, state, title, url)
      if (this.lastLocation !== location.href) this.handleRouteChange()
      return result
    }
  }

  private bindNativeEvents(allowHash: boolean) {
    if (!isBrowser()) return

    window.addEventListener('popstate', () => {
      if (this.lastLocation !== location.href) this.handleRouteChange()
    })

    if (allowHash) {
      window.addEventListener('hashchange', () => {
        if (this.lastLocation !== location.href) this.handleRouteChange()
      })
    }
  }

  private handleRouteChange() {
    // 延时处理，等待路由状态稳定
    if (this.sendTimer) clearTimeout(this.sendTimer)
    this.sendTimer = setTimeout(() => {
      // 上报上一页停留
      this.trackPageStay()

      // 更新状态为新页面
      this.currentUrl = location.href
      this.lastLocation = location.href
      this.pageEnterTime = Date.now()

      // 重置可见性统计
      this.visibilityState = {
        isVisible: !document.hidden,
        accumulatedTime: 0,
        lastVisibleTime: document.hidden ? 0 : Date.now(),
      }
    }, this.delay)
  }

  private initVisibility() {
    if (!isBrowser()) return

    this.visibilityState = {
      isVisible: !document.hidden,
      accumulatedTime: 0,
      lastVisibleTime: document.hidden ? 0 : Date.now(),
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (this.visibilityState.isVisible && this.visibilityState.lastVisibleTime > 0) {
          this.visibilityState.accumulatedTime += Date.now() - this.visibilityState.lastVisibleTime
          this.visibilityState.isVisible = false
          this.visibilityState.lastVisibleTime = 0
        }
      } else {
        this.visibilityState.isVisible = true
        this.visibilityState.lastVisibleTime = Date.now()
      }
    })
  }

  private bindPageUnload() {
    if (!isBrowser()) return

    const handleUnload = () => {
      // 防止 beforeunload 和 pagehide 同时触发导致重复上报
      if (this.hasReportedUnload) return
      this.hasReportedUnload = true
      this.trackPageStayBeacon()
    }

    // 同时监听两者，兼容不同浏览器
    // - pagehide: iOS Safari 推荐，bfcache 友好
    // - beforeunload: 某些旧浏览器只支持这个
    window.addEventListener('pagehide', handleUnload)
    window.addEventListener('beforeunload', handleUnload)
  }

  private calculateStayDuration(): number {
    let total = this.visibilityState.accumulatedTime
    if (this.visibilityState.isVisible && this.visibilityState.lastVisibleTime > 0) {
      total += Date.now() - this.visibilityState.lastVisibleTime
    }
    return total
  }

  /**
   * 路由切换时上报（页面还在，使用常规 event）
   */
  private trackPageStay() {
    if (!this.currentUrl || this.pageEnterTime === 0) return

    const duration = this.calculateStayDuration()
    if (duration < 50) return

    const routerTrackingConfig = this.collector.config.get('routerTracking')
    if (routerTrackingConfig?.autoPageStay === false) return

    const eventData = {
      url: this.currentUrl,
      stay_duration: duration,
      page_title: document.title || '',
    }

    const eventName = routerTrackingConfig?.pageStayEvent || 'page_stay'
    this.collector.event(eventName, eventData)
  }

  /**
   * 页面卸载时上报（使用 beaconEvent，确保数据不丢失）
   */
  private trackPageStayBeacon() {
    if (!this.currentUrl || this.pageEnterTime === 0) return

    const duration = this.calculateStayDuration()
    if (duration < 50) return

    const routerTrackingConfig = this.collector.config.get('routerTracking')
    if (routerTrackingConfig?.autoPageStay === false) return

    const eventData = {
      url: this.currentUrl,
      stay_duration: duration,
      page_title: document.title || '',
    }

    const eventName = routerTrackingConfig?.pageStayEvent || 'page_stay'
    // 页面卸载时使用 beaconEvent，确保数据发送成功
    this.collector.beaconEvent(eventName, eventData)
  }
}
